import { dayOfWeekMap } from "../../../../../../../../components/weekDayPicker/constants";
import { type WeekDays } from "../../../../../../../../enums/weekDays";
import { TimezoneDropdown } from "../../../../../../Common/Fields/timezone-dropdown/TimezoneDropdown";
import { get, isEqual } from "lodash";
import moment from "moment";
import { Grid, Icon } from "semantic-ui-react";
import { ValidatedForm } from "../../../../../../../../components/forms";
import { WeekDayPicker } from "../../../../../../../../components/weekDayPicker";
import dateTimeUtils from "../../../../../../../../utils/dateTimeUtils";
import {
  PeriodicRule,
  PeriodType,
  type RecurrenceSession,
  type SessionPayload,
  type SessionView,
} from "../../../../../types/state";
import { getPropName } from "../../../Utils/getSessionPropName";
import { periodTypes, periodValueOptions } from "../../../constants";

import { ErrorPositions } from "../../../../../../../../enums/errorPositions";
import React, { useMemo } from "react";
import { pluralize } from "../../../../../../../../utils/stringUtils";
import eventSessionUtils from "../../../../../../../../utils/eventSessionUtils";
import { type CommonRecurringSessionBuilderProps } from "../RecurringSessionFormBuilder/RecurringSessionFormBuilder";
import cn from "classnames";

import styles from "./recurringSessionFormBuilderRow.module.scss";

export type RecurringSessionFormBuilderRowProps = CommonRecurringSessionBuilderProps & {
  session: SessionView;
  index: number;
  onRemove: (id: number) => void;
  hideRemove?: boolean;
};

const RecurringSessionFormBuilderRow = (props: RecurringSessionFormBuilderRowProps) => {
  const { session, validatedFieldProps, index, disabled, onRemove, hideRemove, onBlur, onChanged } = props;

  const getRecurrentSessionPropName = (name: keyof RecurrenceSession): string =>
    `${getPropName<SessionPayload>("recurrenceSession", index)}.${name}`;

  const periodValue = session.recurrenceSession?.periodValue ?? 1;
  const periodType = session.recurrenceSession?.periodType ?? PeriodType.Day;
  const { periodicRule } = session.recurrenceSession || {};

  const periodTypeOptions = useMemo(
    () =>
      periodTypes.map((type) => ({
        text: pluralize(PeriodType[type], periodValue),
        value: type,
      })),
    [periodValue],
  );

  const options = useMemo(() => {
    const radioOptions = eventSessionUtils.getRadioOptions(moment.utc(session.startDate), periodType);
    return Object.entries(radioOptions).map(([key, value]) => ({ text: value, value: Number(key) }));
  }, [periodType, session.startDate]);

  const onPeriodTypeChange = async (_: string, value: PeriodType, shouldValidate: boolean) => {
    const recurrenceSessionNew = {
      ...getDefaultRecurrenceData(value, session.startDate),
      seriesEndDate: session.recurrenceSession?.seriesEndDate,
      periodType: value,
      periodValue: session.recurrenceSession?.periodValue || 1,
    };

    await validatedFieldProps.setFieldValue(
      getPropName<SessionPayload>("recurrenceSession", index),
      recurrenceSessionNew,
      shouldValidate,
    );
    onChanged?.();
  };

  return (
    <React.Fragment key={index}>
      <Grid.Row>
        <Grid.Column>
          <div className={styles.repeatEveryBlock}>
            <div>
              <label className={styles.label}>Repeat every</label>
              <div className={styles.periodDropdowns}>
                <ValidatedForm.DropdownField
                  testID="periodValueDropdown"
                  propertyName={getRecurrentSessionPropName("periodValue")}
                  value={session.recurrenceSession?.periodValue}
                  options={periodValueOptions}
                  disabled={disabled}
                  {...validatedFieldProps}
                  handleBlur={() => onBlur?.(getRecurrentSessionPropName("periodValue"))}
                />
                <ValidatedForm.DropdownField
                  testID="periodTypeDropdown"
                  propertyName={getRecurrentSessionPropName("periodType")}
                  value={session.recurrenceSession?.periodType}
                  options={periodTypeOptions}
                  onBlur={onBlur}
                  disabled={disabled}
                  {...validatedFieldProps}
                  setFieldValue={onPeriodTypeChange}
                />
              </div>
            </div>

            <div className={styles.dynamicField}>
              {periodValue === 1 && periodType === PeriodType.Day && (
                <WeekDayPicker selectedPredicate={(_) => true} disabled />
              )}
              {periodType === PeriodType.Week && (
                <ValidatedForm.WeekDayPickerField
                  propertyName={getRecurrentSessionPropName("weekDays")}
                  value={session.recurrenceSession?.weekDays}
                  selectedPredicate={(x) => !!session.recurrenceSession?.weekDays?.some((wd) => wd === dayOfWeekMap[x])}
                  onBlur={onBlur}
                  disabled={disabled}
                  minSelectionCount={1}
                  {...validatedFieldProps}
                />
              )}
              {(periodType === PeriodType.Month || periodType === PeriodType.Year) && (
                <ValidatedForm.RadioGroupField
                  className={styles.radioGroup}
                  propertyName={getRecurrentSessionPropName("periodicRule")}
                  value={periodicRule}
                  options={options}
                  onBlur={onBlur}
                  disabled={disabled}
                  {...validatedFieldProps}
                />
              )}
            </div>
            {!hideRemove && (
              <Icon
                data-testid="remove-icon"
                disabled={disabled}
                className={cn(styles.removeIcon, "minus circle icon")}
                size="big"
                color="blue"
                onClick={() => onRemove(index)}
              />
            )}
          </div>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <div className={styles.containerFields}>
            <div>
              <ValidatedForm.TimePickerField
                className={styles.baseField}
                label="Starts at"
                selected={session.startsAt || ""}
                propertyName={getPropName<SessionView>("startsAt", index)}
                markAsRequired
                disabled={disabled}
                errorPosition={ErrorPositions.bottom}
                {...validatedFieldProps}
                onBlur={() => onBlur(getPropName<SessionPayload>("startsAtTicksMinutes", index))}
              />
            </div>
            <div>
              <ValidatedForm.TimePickerField
                className={styles.baseField}
                label="Ends at"
                selected={session.endsAt || ""}
                propertyName={getPropName<SessionView>("endsAt", index)}
                markAsRequired
                disabled={disabled}
                errorPosition={ErrorPositions.bottom}
                {...validatedFieldProps}
                onBlur={() => onBlur(getPropName<SessionPayload>("endsAtTicksMinutes", index))}
              />
            </div>
            <div className={styles.timezoneFieldWrapper}>
              <TimezoneDropdown
                label="Time zone"
                propertyName={getPropName<SessionView>("timeZone", index)}
                value={session.timeZone || ""}
                markAsRequired
                disabled={disabled}
                error=""
                icon={<Icon className="fa-clock" />}
                {...validatedFieldProps}
                onBlur={onBlur}
                touched={get(validatedFieldProps.touched, getPropName<SessionView>("timeZone", index))}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid.Row>
    </React.Fragment>
  );
};

/* istanbul ignore next */
const getNeededProps = (props: RecurringSessionFormBuilderRowProps) => {
  const { arrayHelpers, validatedFieldProps, onRemove, ...rest } = props;

  const errors = get(props.validatedFieldProps.errors, `sessions.${props.index}`);
  const touched = get(props.validatedFieldProps.touched, `sessions.${props.index}`);

  return { ...rest, errors, touched };
};

/* istanbul ignore next */
export default React.memo(RecurringSessionFormBuilderRow, (prev, next) => {
  return isEqual(getNeededProps(prev), getNeededProps(next));
});

const getDefaultRecurrenceData = (value: PeriodType, startDate: string | null) => {
  switch (value) {
    case PeriodType.Week: {
      const startWeekDay = dateTimeUtils.getWeekDayName(moment.utc(startDate));
      return { weekDays: [dayOfWeekMap[startWeekDay as WeekDays]] };
    }
    case PeriodType.Month: {
      return { periodicRule: PeriodicRule.MonthlyOnExactDayInMonth };
    }
    case PeriodType.Year: {
      return { periodicRule: PeriodicRule.YearlyOnExactDayAndMonth };
    }
    default: {
      return {};
    }
  }
};
